import React from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faPen, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { MonospacedInput } from "src/base-components/MonospacedInput";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { IconButton } from "src/design-system/Icon";
import {
  HIGHLIGHT_CLASSES,
  useIsFieldHighlighted,
} from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import {
  ParameterForm,
  ParametersForm,
} from "src/globalParameters/GlobalParametersList";
import { HighlightParametersToggleButton } from "src/globalParameters/HighlightParametersToggleButton";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

export type GlobalParametersCardProps = {
  parameter: ParameterForm;
  index: number;
  id: string;
  onDelete: () => void;
  onEdit: () => void;
  onEditValue: () => void;
  readonly: boolean;
};

export const GlobalParametersCard: React.FC<GlobalParametersCardProps> = ({
  parameter,
  index,
  id,
  readonly,
  onDelete,
  onEdit,
  onEditValue,
}) => {
  const { register } = useFormContext<ParametersForm>();
  const { listeners, setNodeRef, transform, transition } = useSortable({
    id: id,
  });

  const isSearchHighlighted = useIsFieldHighlighted(parameter.name);
  const [outerRef] = useHighlightedScrollIntoView(isSearchHighlighted);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const baseDataLoc = `${parameter.name}-card`;

  return (
    <div
      ref={setNodeRef}
      className={twJoin(
        "relative w-full",
        "first:rounded-t last:rounded-b",
        !readonly && "hover:bg-gray-50",
      )}
      data-loc={baseDataLoc}
      style={style}
    >
      <div
        className={twJoin(
          "rounded-[inherit] border pb-4",
          !isSearchHighlighted && "border-transparent",
          isSearchHighlighted && HIGHLIGHT_CLASSES,
        )}
      >
        <div className="mt-4 pr-4 pl-9">
          <div className="mb-2">
            <p
              className={twJoin(
                "mb-0.5 truncate font-inter-semibold-13px text-gray-800",
                readonly ? "pr-24" : "pr-40",
              )}
              data-loc={`${baseDataLoc}-name`}
            >
              {parameter.name}
            </p>
            {parameter.description && (
              <p
                className="line-clamp-3 w-66 font-inter-normal-12px text-gray-500"
                data-loc={`${baseDataLoc}-description`}
              >
                {parameter.description}
              </p>
            )}
          </div>
          <div ref={outerRef} onClick={!readonly ? onEditValue : undefined}>
            <MonospacedInput
              className={twJoin(!readonly && "cursor-pointer")}
              containerClassName="grow"
              data-loc={`${baseDataLoc}-value`}
              formProps={register(`params.${index}.expr`)}
              placeholder="Enter Parameter value here..."
              codeColors
              disabled
            />
          </div>
        </div>
        <ReorderHandle
          className="absolute top-3 left-3 pt-1"
          immutable={readonly}
          listeners={listeners}
        />
        <div className="absolute top-3 right-3 flex h-5 flex-row items-center gap-1">
          <HighlightParametersToggleButton
            disabled={readonly}
            parameterName={parameter.name}
          />
          <IconButton
            disabled={readonly}
            icon={faPen}
            size="xs"
            onClick={onEdit}
          />
          <IconButton
            disabled={readonly}
            icon={faTrashCan}
            size="xs"
            onClick={onDelete}
          />
        </div>
      </div>
    </div>
  );
};
